import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';

/**
 * Alta y edición de un comercio — PÁGINA con ruta propia.
 *
 * Era un modal de 3 pasos. En una página el asistente por pasos no aporta:
 * esconde campos que ya caben a la vista y obliga a ir y volver para revisar.
 * Van las tres secciones abiertas y la barra de guardar pegada al pie.
 *
 * Rutas: `console/merchants/nuevo` · `console/merchants/:code/editar`
 */
@Component({
  selector: 'app-comercios-form',
  templateUrl: './comercios-form.component.html',
  styleUrls: ['./comercios-form.component.css']
})
export class ComerciosFormComponent implements OnInit {

  editando = false;
  codigo = '';
  loading = false;
  guardando = false;
  noEncontrado = false;

  /**
   * 🔴 «Cuenta de liquidación» APAGADA por pedido del dueño del producto
   * (2026-08-21). No se borra: el bloque queda escrito y el dato se sigue
   * cargando y guardando si ya existe — apagarla no puede BORRAR la cuenta de
   * un comercio que ya cobra.
   *
   * 📍 Y hay un motivo de fondo para que no esté acá: cambiar a dónde se le
   * transfiere a un comercio es el vector de fraude más simple que existe, y
   * merece su propio camino con confirmación, no un campo más en un formulario
   * largo.
   */
  readonly MOSTRAR_CUENTA = false;

  /** Las piezas del domicilio fiscal, con los rótulos del portal del comercio. */
  readonly PIEZAS_FISCAL: Array<{ clave: string; rotulo: string; obligatorio: boolean }> = [
    { clave: 'street', rotulo: 'Calle, avenida, edificio, local', obligatorio: true },
    { clave: 'city', rotulo: 'Ciudad', obligatorio: true },
    { clave: 'municipality', rotulo: 'Municipio', obligatorio: false },
    { clave: 'state', rotulo: 'Estado', obligatorio: false },
    { clave: 'postal_code', rotulo: 'Código postal', obligatorio: false },
  ];

  formData: any = {
    code: '', name: '', legal_name: '', tax_id: '',
    fiscal: { street: '', city: '', municipality: '', state: '', postal_code: '' },
    // Datos de contacto DEL COMERCIO (no de una persona): estaban en el modelo
    // y en el portal, pero no se podían cargar desde acá.
    phone: '', email: '', website: '',
    bank_account: { bank: null, account_number: '', account_type: 'CORRIENTE', holder: '', tax_id: '' }
  };

  /** Los adjuntos del comercio: RIF, registro mercantil, cédula, logo. */
  documentos: any[] = [];
  subiendo: string | null = null;
  readonly TIPOS_ADJUNTO = [
    { kind: 'RIF',                label: 'RIF',                 obligatorio: true },
    { kind: 'REGISTRO_MERCANTIL', label: 'Registro mercantil',  obligatorio: false },
    { kind: 'CEDULA_REP',         label: 'Cédula del representante', obligatorio: false },
    { kind: 'LOGO',               label: 'Logo',                obligatorio: false },
    { kind: 'OTRO',               label: 'Otro documento',      obligatorio: false },
  ];

  contactos: any[] = [];
  contactoActual = { name: '', lastname: '', phone: '', email: '', position: '' };
  editandoContacto: number | null = null;

  /** Con el almacenamiento local el núcleo guarda la ruta sola (`/uploads/…`): se sirve desde el núcleo. */
  urlArchivo(url: string): string { return url && url.startsWith('/') ? `${environment.BASE_LA}${url}` : url; }

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void {
    const code = this.ruta.snapshot.paramMap.get('code');
    this.editando = !!code;
    this.codigo = code || '';
    if (this.editando) this.cargar();
  }

  puede = (accion: string): boolean => this._auth.hasPermission(`config.comercios.${accion}`);

  cargar = async (): Promise<void> => {
    this.loading = true;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      const m = rep?.merchant;
      if (!m) { this.noEncontrado = true; return; }
      this.formData = {
        code: m.code, name: m.name || '', legal_name: m.legal_name || '',
        tax_id: m.tax_id || '',
        // Domicilio fiscal en piezas; un comercio viejo solo trae `address`: va como calle.
        fiscal: {
          street: (m.fiscal?.street || '').trim() ? m.fiscal.street : (m.address || ''),
          city: m.fiscal?.city || '', municipality: m.fiscal?.municipality || '',
          state: m.fiscal?.state || '', postal_code: m.fiscal?.postal_code || '',
        },
        phone: m.phone || '', email: m.email || '', website: m.website || '',
        bank_account: m.bank_account ||
          { bank: null, account_number: '', account_type: 'CORRIENTE', holder: '', tax_id: '' }
      };
      this.contactos = JSON.parse(JSON.stringify(m.contacts || []));
      this.documentos = m.documents || [];
    } catch {
      this.noEncontrado = true;
    } finally {
      this.loading = false;
    }
  };

  // ── validación ──────────────────────────────────────────────────────────────

  /**
   * Sin código, nombre y RIF no hay comercio. El resto se completa después.
   *
   * 🔴 Y AL CREAR hace falta además un contacto con correo, que antes no.
   *
   * De ese contacto sale el PRIMER USUARIO del comercio, el que entra al portal.
   * Sin él, el comercio nace sin poder entrar — y eso no se ve por ningún lado:
   * no hay pantalla que diga «este comercio no puede entrar». Pedirlo acá es más
   * barato que descubrir dos semanas después que nunca pudo.
   *
   * ⚠️ Al EDITAR no se exige: un comercio que ya existe puede haberse creado
   * antes de esta regla, y bloquearle la edición por eso lo dejaría sin poder
   * arreglar ningún otro dato.
   */
  get valido(): boolean {
    const base = !!(this.formData.code?.trim() && this.formData.name?.trim() && this.formData.tax_id?.trim()
      && this.formData.fiscal?.street?.trim() && this.formData.fiscal?.city?.trim());
    return this.editando ? base : base && this.tieneContactoConCorreo;
  }

  /** El contacto del que va a salir el primer usuario. */
  get tieneContactoConCorreo(): boolean {
    return this.contactos.some((c: any) => (c?.email || '').trim() && (c?.name || '').trim());
  }

  /** Lo que falta para poder crear, dicho con todas las letras. */
  get faltaParaCrear(): string {
    if (this.editando) return '';
    if (!this.formData.code?.trim()) return 'Falta el código del comercio';
    if (!this.formData.name?.trim()) return 'Falta el nombre';
    if (!this.formData.tax_id?.trim()) return 'Falta el RIF';
    if (!this.formData.fiscal?.street?.trim()) return 'Falta la calle del domicilio fiscal';
    if (!this.formData.fiscal?.city?.trim()) return 'Falta la ciudad del domicilio fiscal';
    if (!this.tieneContactoConCorreo) return 'Falta un contacto con nombre y correo: de ahí sale el usuario que entra al portal';
    return '';
  }

  get tieneCuenta(): boolean { return !!this.formData.bank_account?.account_number?.trim(); }

  /** Se ordena al salir del campo, no mientras se escribe. */
  normalizarRif = (campo: 'tax_id' | 'cuenta'): void => {
    const actual = campo === 'tax_id' ? this.formData.tax_id : this.formData.bank_account.tax_id;
    const crudo = (actual || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    let out = '';
    if (crudo) {
      const letra = /^[JGVEP]/.test(crudo) ? crudo[0] : 'J';
      const digitos = crudo.replace(/^[JGVEP]/, '').replace(/\D/g, '');
      out = digitos.length > 1
        ? `${letra}-${digitos.slice(0, -1)}-${digitos.slice(-1)}`
        : `${letra}-${digitos}`;
    }
    if (campo === 'tax_id') this.formData.tax_id = out;
    else this.formData.bank_account.tax_id = out;
  };

  // ── contactos ───────────────────────────────────────────────────────────────

  agregarContacto = (): void => {
    const c = this.contactoActual;
    if (!c.name?.trim() || !c.email?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Falta el nombre o el correo',
                  text: 'Los dos son obligatorios para poder guardar un contacto.' });
      return;
    }
    const correo = c.email.trim().toLowerCase();
    const repetido = this.contactos.some((x, i) =>
      (x.email || '').trim().toLowerCase() === correo && i !== this.editandoContacto);
    if (repetido) {
      Swal.fire({ icon: 'warning', title: 'Ese correo ya está',
                  text: 'No se admiten dos contactos con el mismo correo.' });
      return;
    }
    if (this.editandoContacto !== null) this.contactos[this.editandoContacto] = { ...c };
    else this.contactos.push({ ...c });
    this.contactoActual = { name: '', lastname: '', phone: '', email: '', position: '' };
    this.editandoContacto = null;
  };

  editarContacto = (i: number): void => {
    this.contactoActual = { ...this.contactos[i] };
    this.editandoContacto = i;
  };

  cancelarEdicionContacto = (): void => {
    this.contactoActual = { name: '', lastname: '', phone: '', email: '', position: '' };
    this.editandoContacto = null;
  };

  quitarContacto = (i: number): void => {
    this.contactos.splice(i, 1);
    if (this.editandoContacto === i) this.cancelarEdicionContacto();
  };

  // ── adjuntos ────────────────────────────────────────────────────────────────
  // 🔴 Sólo al EDITAR. El endpoint es `/:code/documentos`, o sea que hace falta
  // que el comercio exista: no hay dónde colgar un archivo de algo que todavía
  // no se creó. En el alta se dice con todas las letras en vez de mostrar un
  // botón que va a fallar.

  documentosDe = (kind: string): any[] => this.documentos.filter(d => d.kind === kind);

  get faltaRif(): boolean { return this.documentosDe('RIF').length === 0; }

  subirAdjunto = async (kind: string, evento: any): Promise<void> => {
    const archivo: File = evento?.target?.files?.[0];
    if (!archivo) return;
    // Se limpia el input SIEMPRE, aunque falle: si no, elegir el mismo archivo
    // otra vez no dispara el `change` y parece que el botón dejó de andar.
    const input = evento.target;
    this.subiendo = kind;
    try {
      const rep = await this._merchant.subirDocumento(this.codigo, kind, archivo);
      // 🔴 El back devuelve `{ ok, documento, avance }` — NO la lista completa,
      // y el documento viene con `id`, no con `_id`. Sin este mapeo el archivo
      // recién subido no aparecía hasta recargar la pantalla, y el botón de
      // quitar no encontraba el suyo. Se agrega a mano en vez de releer el
      // comercio: releer pisaría lo que se esté escribiendo en el formulario.
      const d = rep?.documento;
      if (d) this.documentos = [...this.documentos, { ...d, _id: d.id }];
      Swal.fire({ icon: 'success', title: 'Adjunto cargado', timer: 1400, showConfirmButton: false });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cargar el adjunto',
                  text: e?.error?.msg || e?.error?.detalle || 'Revisa el formato y el tamaño.' });
    } finally {
      this.subiendo = null;
      if (input) input.value = '';
    }
  };

  quitarAdjunto = async (doc: any): Promise<void> => {
    const r = await Swal.fire({
      icon: 'question', title: '¿Quitar el adjunto?',
      text: doc?.name || '', showCancelButton: true,
      confirmButtonText: 'Quitar', cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    try {
      await this._merchant.borrarDocumento(this.codigo, doc._id);
      // El back devuelve `{ ok, avance }`, así que la lista se ajusta acá.
      this.documentos = this.documentos.filter(d => d._id !== doc._id);
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo quitar el adjunto',
                  text: e?.error?.msg || 'Intenta de nuevo.' });
    }
  };

  pesoLegible = (bytes: number): string => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
  };

  // ── guardar y salir ─────────────────────────────────────────────────────────

  guardar = async (): Promise<void> => {
    if (!this.valido || this.guardando) return;
    this.guardando = true;
    try {
      const datos: any = {
        name: this.formData.name.trim(),
        legal_name: (this.formData.legal_name || '').trim(),
        tax_id: this.formData.tax_id.trim(),
        // El núcleo arma `address` (texto plano) a partir de `fiscal`: ya no se manda.
        fiscal: Object.fromEntries(this.PIEZAS_FISCAL.map((p) => [p.clave, String(this.formData.fiscal?.[p.clave] || '').trim()])),
        phone: (this.formData.phone || '').trim(),
        email: (this.formData.email || '').trim().toLowerCase(),
        website: (this.formData.website || '').trim(),
        contacts: this.contactos
      };
      if (this.tieneCuenta) datos.bank_account = this.formData.bank_account;

      let destino = this.codigo;
      if (this.editando) {
        // 🔴 El código no viaja en el update: el back lo rechaza con 400.
        await this._merchant.updateMerchant(this.codigo, datos);
      } else {
        datos.code = this.formData.code.trim().toUpperCase();
        const r: any = await this._merchant.createMerchant(datos);
        destino = datos.code;

        // 🔴 La clave inicial se muestra UNA vez y sin temporizador.
        //
        // El alta crea el primer usuario del comercio, y este es el único
        // momento en que alguien ve con qué entra. Un cartel que se va solo a
        // los 1,6 segundos —como el de «comercio creado»— haría que se pierda
        // exactamente el dato que hay que anotar y dictar.
        if (r?.usuario?.username) {
          await Swal.fire({
            icon: 'success',
            title: 'Comercio creado',
            html: `<p>Ya puede entrar al portal con:</p>
                   <p style="font-size:1.25rem;margin:.75rem 0">
                     <strong>${r.usuario.username}</strong><br>
                     <strong>${r.clave_inicial || 'admin123'}</strong>
                   </p>
                   <p style="font-size:.85rem">El sistema le va a exigir cambiar la clave al entrar.</p>`,
            confirmButtonText: 'Anotado'
          });
        } else {
          // El comercio se creó pero su usuario no. No es un error del alta:
          // el comercio existe y es válido, y el usuario se crea después desde
          // la sección Usuarios. Se dice cuál fue el motivo.
          await Swal.fire({
            icon: 'warning',
            title: 'Comercio creado, sin usuario',
            text: (r?.avisos || []).join(' · ') || 'Créale el usuario desde la sección Usuarios del comercio.',
            confirmButtonText: 'Entendido'
          });
        }
        this.router.navigate(['/console/merchants', destino]);
        return;
      }

      await Swal.fire({
        icon: 'success',
        title: 'Comercio actualizado',
        timer: 1600, showConfirmButton: false
      });
      this.router.navigate(['/console/merchants', destino]);
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar el comercio',
                  text: e?.error?.msg || e?.message || 'Intenta de nuevo.' });
    } finally {
      this.guardando = false;
    }
  };

  /** Salir sin guardar avisa: el formulario es largo y se pierde lo escrito. */
  cancelar = async (): Promise<void> => {
    const r = await Swal.fire({
      icon: 'question', title: '¿Salir sin guardar?',
      text: 'Se pierde lo que hayas escrito.',
      showCancelButton: true, confirmButtonText: 'Salir', cancelButtonText: 'Seguir editando'
    });
    if (r.isConfirmed) this.volver();
  };

  volver = (): void => {
    if (this.editando) this.router.navigate(['/console/merchants', this.codigo]);
    else this.router.navigate(['/console/merchants']);
  };
}
